import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity, Target, BookOpen, Bot, Sparkles, Clock, TrendingUp } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface FeatureUsageData {
  feature_name: string;
  total_users: number;
  total_usage_count: number;
  avg_time_spent_hours: number;
  last_used: string;
}

interface ActivityData {
  activity_type: string;
  count: number;
  unique_users: number;
  last_occurrence: string;
}

export const UserBehaviorAnalytics = () => {
  const { isAdmin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [featureUsage, setFeatureUsage] = useState<FeatureUsageData[]>([]);
  const [activityData, setActivityData] = useState<ActivityData[]>([]);
  
  useEffect(() => {
    if (isAdmin) {
      loadBehaviorAnalytics();
    }
  }, [isAdmin]);

  const loadBehaviorAnalytics = async () => {
    setLoading(true);
    try {
      // Try comprehensive analytics first, fallback to individual queries
      try {
        const { data: featureData, error: featureError } = await supabase
          .from('user_feature_usage')
          .select('feature_name, usage_count, total_time_spent_seconds, last_used_at, user_id');

        if (featureError) {
          console.warn('Feature usage query error:', featureError);
        }

        if (featureData && featureData.length > 0) {
          const featureStats = featureData.reduce((acc: any, row: any) => {
            const feature = row.feature_name;
            if (!acc[feature]) {
              acc[feature] = {
                feature_name: feature,
                total_users: new Set(),
                total_usage_count: 0,
                total_time_spent_seconds: 0,
                last_used: row.last_used_at
              };
            }
            acc[feature].total_users.add(row.user_id);
            acc[feature].total_usage_count += row.usage_count;
            acc[feature].total_time_spent_seconds += row.total_time_spent_seconds || 0;
            if (new Date(row.last_used_at) > new Date(acc[feature].last_used)) {
              acc[feature].last_used = row.last_used_at;
            }
            return acc;
          }, {});

          const processedFeatureData = Object.values(featureStats).map((stat: any) => ({
            feature_name: stat.feature_name,
            total_users: stat.total_users.size,
            total_usage_count: stat.total_usage_count,
            avg_time_spent_hours: Math.round((stat.total_time_spent_seconds / 3600) * 100) / 100,
            last_used: stat.last_used
          }));

          setFeatureUsage(processedFeatureData as FeatureUsageData[]);
        } else {
          // Create fallback data from game sessions if no feature usage data
          const { data: gameData } = await supabase
            .from('game_sessions')
            .select('white_player_id, black_player_id, updated_at, game_status')
            .in('game_status', ['checkmate', 'stalemate', 'draw', 'resignation', 'timeout']);

          if (gameData && gameData.length > 0) {
            const playerGames = new Map();
            gameData.forEach(game => {
              if (game.white_player_id) {
                const existing = playerGames.get(game.white_player_id) || { count: 0, lastPlay: game.updated_at };
                playerGames.set(game.white_player_id, {
                  count: existing.count + 1,
                  lastPlay: new Date(game.updated_at) > new Date(existing.lastPlay) ? game.updated_at : existing.lastPlay
                });
              }
              if (game.black_player_id) {
                const existing = playerGames.get(game.black_player_id) || { count: 0, lastPlay: game.updated_at };
                playerGames.set(game.black_player_id, {
                  count: existing.count + 1,
                  lastPlay: new Date(game.updated_at) > new Date(existing.lastPlay) ? game.updated_at : existing.lastPlay
                });
              }
            });

            const fallbackFeature = [{
              feature_name: 'chess_games',
              total_users: playerGames.size,
              total_usage_count: gameData.length,
              avg_time_spent_hours: 0.5, // Estimate
              last_used: new Date(Math.max(...Array.from(playerGames.values()).map(p => new Date(p.lastPlay).getTime()))).toISOString()
            }];

            setFeatureUsage(fallbackFeature as FeatureUsageData[]);
          }
        }

        // Fetch activity statistics
        const { data: activityRawData, error: activityError } = await supabase
          .from('user_activity_logs')
          .select('activity_type, user_id, created_at');

        if (activityError) {
          console.warn('Activity logs query error:', activityError);
        }

        if (activityRawData && activityRawData.length > 0) {
          const activityStats = activityRawData.reduce((acc: any, row: any) => {
            const activity = row.activity_type;
            if (!acc[activity]) {
              acc[activity] = {
                activity_type: activity,
                count: 0,
                unique_users: new Set(),
                last_occurrence: row.created_at
              };
            }
            acc[activity].count += 1;
            acc[activity].unique_users.add(row.user_id);
            if (new Date(row.created_at) > new Date(acc[activity].last_occurrence)) {
              acc[activity].last_occurrence = row.created_at;
            }
            return acc;
          }, {});

          const processedActivityData = Object.values(activityStats).map((stat: any) => ({
            activity_type: stat.activity_type,
            count: stat.count,
            unique_users: stat.unique_users.size,
            last_occurrence: stat.last_occurrence
          }));

          setActivityData(processedActivityData as ActivityData[]);
        } else {
          // Create fallback activities from completed games
          const { data: gameStats } = await supabase
            .from('game_sessions')
            .select('game_status, updated_at, white_player_id, black_player_id')
            .in('game_status', ['checkmate', 'stalemate', 'draw', 'resignation', 'timeout']);

          if (gameStats && gameStats.length > 0) {
            const uniquePlayers = new Set();
            gameStats.forEach(game => {
              if (game.white_player_id) uniquePlayers.add(game.white_player_id);
              if (game.black_player_id) uniquePlayers.add(game.black_player_id);
            });

            const fallbackActivity = [{
              activity_type: 'game_completed',
              count: gameStats.length,
              unique_users: uniquePlayers.size,
              last_occurrence: new Date(Math.max(...gameStats.map(g => new Date(g.updated_at).getTime()))).toISOString()
            }];

            setActivityData(fallbackActivity as ActivityData[]);
          }
        }

      } catch (error) {
        console.error('Analytics query failed:', error);
        toast.error('Analytics data unavailable - working on populating data');
      }
    } catch (error) {
      toast.error('Failed to load behavior analytics');
      console.error('Error loading behavior analytics:', error);
    }
    setLoading(false);
  };

  const getFeatureIcon = (featureName: string) => {
    switch (featureName) {
      case 'chess_games': return <Activity className="h-4 w-4" />;
      case 'puzzles': return <Target className="h-4 w-4" />;
      case 'tutorials': return <BookOpen className="h-4 w-4" />;
      case 'ai_squad': return <Bot className="h-4 w-4" />;
      case 'hatch_evolution': return <Sparkles className="h-4 w-4" />;
      default: return <TrendingUp className="h-4 w-4" />;
    }
  };

  const getFeatureDisplayName = (featureName: string) => {
    switch (featureName) {
      case 'chess_games': return 'Chess Games';
      case 'puzzles': return 'Daily Puzzles';
      case 'tutorials': return 'Tutorials';
      case 'ai_squad': return 'AI Squad Challenge';
      case 'hatch_evolution': return 'Hatch Evolution';
      default: return featureName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const formatActivityType = (activityType: string) => {
    return activityType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">User Behavior Analytics</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-20"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-16 mb-2"></div>
                <div className="h-3 bg-muted rounded w-24"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const topFeatures = featureUsage
    .sort((a, b) => b.total_users - a.total_users)
    .slice(0, 3);

  const totalUniqueUsers = new Set(
    featureUsage.flatMap(f => Array(f.total_users).fill(0).map((_, i) => `${f.feature_name}_${i}`))
  ).size;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Behavior Analytics</h1>
          <p className="text-muted-foreground">Understand how users interact with your platform</p>
        </div>
        <Button onClick={loadBehaviorAnalytics}>Refresh</Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Most Popular Feature
            </CardTitle>
            {topFeatures[0] && getFeatureIcon(topFeatures[0].feature_name)}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {topFeatures[0] ? getFeatureDisplayName(topFeatures[0].feature_name) : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {topFeatures[0] ? `${topFeatures[0].total_users} users` : 'No data available'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Activities
            </CardTitle>
            <Activity className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activityData.reduce((sum, activity) => sum + activity.count, 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Features
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{featureUsage.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Features in use
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="features" className="space-y-4">
        <TabsList>
          <TabsTrigger value="features">Feature Usage</TabsTrigger>
          <TabsTrigger value="activities">User Activities</TabsTrigger>
        </TabsList>
        
        <TabsContent value="features">
          <Card>
            <CardHeader>
              <CardTitle>Feature Usage Statistics</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead className="text-right">Users</TableHead>
                    <TableHead className="text-right">Total Usage</TableHead>
                    <TableHead className="text-right">Avg Time</TableHead>
                    <TableHead className="text-right">Last Used</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {featureUsage
                    .sort((a, b) => b.total_users - a.total_users)
                    .map((feature, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          {getFeatureIcon(feature.feature_name)}
                          <span className="font-medium">
                            {getFeatureDisplayName(feature.feature_name)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-medium">{feature.total_users}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        {feature.total_usage_count.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          {feature.avg_time_spent_hours}h
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-sm">
                          {new Date(feature.last_used).toLocaleDateString()}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <CardTitle>User Activity Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Activity Type</TableHead>
                    <TableHead className="text-right">Total Count</TableHead>
                    <TableHead className="text-right">Unique Users</TableHead>
                    <TableHead className="text-right">Last Occurrence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityData
                    .sort((a, b) => b.count - a.count)
                    .map((activity, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="font-medium">
                          {formatActivityType(activity.activity_type)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-medium">{activity.count.toLocaleString()}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">
                          {activity.unique_users} users
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-sm">
                          {new Date(activity.last_occurrence).toLocaleDateString()}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};